import { z } from 'zod';
import { ModerationVerdict } from './enums.ts';

/**
 * Showcase manifest — the seam between the generation pipeline and the public
 * landing.
 *
 * The pipeline produces media; the landing renders it. This manifest is the ONLY
 * thing both sides touch, which buys two things:
 *
 *  - the landing can be built against a fixture while the corpus is still
 *    generating, and
 *  - regenerating the corpus is a data change, never a frontend change.
 *
 * The marketing surface therefore holds **no knowledge of how the media was
 * produced** — it reads `media`, `category`, `prompt`/`style` and `deepLink`, and
 * nothing else. Everything here is plain JSON (ISO date strings, no functions), so
 * the pipeline can emit it as a file and re-validate it with this same schema.
 *
 * Categories are DATA (see {@link ShowcaseCategory}): adding one to the corpus adds
 * a tab, with no code change on either side.
 *
 * Compliance: `moderation` is REQUIRED on every item, and {@link isPublishable}
 * decides whether an item may be served from a crawlable origin. Recording a
 * verdict and acting on it are deliberately separate — the same posture
 * `scripts/companions/catalog-art.ts` takes for the prebuilt catalog: state the
 * machine's real answer, then gate publication on it, never launder one into the
 * other. See docs/idea/compliance.md.
 */

/** A stable, URL-safe editorial identifier (item ids, category keys). */
const slug = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'must be lowercase kebab-case');

/**
 * What an item advertises. Wider than `MediaKind` (`image | video`, a database
 * column contract) because the wall also shows a *companion* — a persona rather
 * than a single rendered file. Kept as its own enum so widening the showcase can
 * never widen a DB column by accident.
 */
export const ShowcaseKind = z.enum(['image', 'video', 'companion']);
export type ShowcaseKind = z.infer<typeof ShowcaseKind>;

/**
 * Which surface an item is cleared for.
 *
 * `public` = servable from the crawlable origin with no age gate in front of it;
 * `gated` = behind the age gate only. Carrying this as data means the open
 * question of how explicit the public landing may be is answered by editing a
 * manifest, not by editing components — this schema deliberately does NOT decide
 * it.
 */
export const ShowcaseAudience = z.enum(['public', 'gated']);
export type ShowcaseAudience = z.infer<typeof ShowcaseAudience>;

/**
 * How an item earned (or failed to earn) the right to be published.
 *
 * - `classifier` — the output classifier returned a clearing verdict on these bytes.
 * - `human-review` — a person reviewed these exact bytes and signed off.
 * - `unreviewed` — nothing has cleared these bytes yet. An honest state, not a
 *   failure state: a fixture or a freshly generated item legitimately sits here
 *   until it is gated. It must never reach a published manifest — that is what
 *   {@link isPublishable} is for.
 */
export const ShowcaseClearance = z.enum(['classifier', 'human-review', 'unreviewed']);
export type ShowcaseClearance = z.infer<typeof ShowcaseClearance>;

/** A SHA-256 hex digest — binds a clearance to the EXACT bytes it was granted on. */
const sha256Hex = z.string().regex(/^[0-9a-f]{64}$/, 'must be a lowercase hex SHA-256 digest');

/**
 * The output classifier's own answer, in the shape the media worker denormalizes
 * onto `media_assets.nsfw_verdict`. Recorded verbatim, including an abstain — the
 * column documents itself as the MACHINE's verdict, so it never holds a human's.
 */
export const ShowcaseClassifierVerdict = z.object({
  /**
   * The pipeline's own {@link ModerationVerdict}, reused rather than restated —
   * a manifest that could record a verdict the gate cannot produce would not be
   * recording the gate's answer.
   */
  verdict: ModerationVerdict,
  categories: z.array(z.string().min(1).max(64)).max(32).default([]),
  model: z.string().min(1).max(128),
});
export type ShowcaseClassifierVerdict = z.infer<typeof ShowcaseClassifierVerdict>;

/**
 * The only machine verdict that CLEARS bytes. `review` is an abstain and `block`
 * is a refusal; both gate. Named so the rule is data rather than a sentence in a
 * doc comment that nothing enforces.
 */
export const CLEARING_VERDICT: ModerationVerdict = 'allow';

/**
 * A person's sign-off on specific bytes — the 2257-style record-keeping posture
 * from docs/idea/compliance.md, not an anonymous literal. Bound to the payload by
 * {@link ShowcaseModeration.sha256}, so swapping the file without re-reviewing it
 * cannot inherit the clearance.
 */
export const ShowcaseHumanReview = z.object({
  /** Who signed off — a role or named custodian, never an empty attribution. */
  reviewer: z.string().min(1).max(200),
  /** ISO date (`YYYY-MM-DD`) the review happened. */
  reviewedAt: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'must be an ISO date (YYYY-MM-DD)'),
  /** What was actually checked, in prose that a reviewer can be held to. */
  statement: z.string().min(1).max(2000),
});
export type ShowcaseHumanReview = z.infer<typeof ShowcaseHumanReview>;

/**
 * The compliance record for one item's bytes. Required on every item so that
 * "did anything clear this?" is answerable structurally rather than by asking
 * whoever built the corpus.
 */
export const ShowcaseModeration = z
  .object({
    sha256: sha256Hex,
    clearance: ShowcaseClearance,
    /** The machine's answer, when a classifier ran at all. */
    verdict: ShowcaseClassifierVerdict.optional(),
    /** The human sign-off, when a person cleared these bytes. */
    review: ShowcaseHumanReview.optional(),
  })
  .superRefine((moderation, ctx) => {
    if (moderation.clearance === 'classifier' && !moderation.verdict) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['verdict'],
        message: "clearance 'classifier' requires the classifier verdict it was granted on",
      });
    }
    // Recording a refusal faithfully and then calling it a clearance is the one
    // way this record could launder a `block` onto a public URL. A gating verdict
    // means the item is not machine-cleared — say `unreviewed`, or get a human.
    if (
      moderation.clearance === 'classifier' &&
      moderation.verdict &&
      moderation.verdict.verdict !== CLEARING_VERDICT
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['verdict', 'verdict'],
        message: `clearance 'classifier' requires a '${CLEARING_VERDICT}' verdict, not '${moderation.verdict.verdict}'`,
      });
    }
    if (moderation.clearance === 'human-review' && !moderation.review) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['review'],
        message: "clearance 'human-review' requires the sign-off record it was granted on",
      });
    }
    // A person may resolve an ABSTAIN — that is what `review` means, and it is how
    // the prebuilt catalog is cleared offline. A `block` is not an abstain: it is
    // the red-line refusal, and `scripts/companions/catalog-art.ts` aborts the seed
    // on one rather than uploading. No sign-off outranks it here either.
    if (moderation.clearance === 'human-review' && moderation.verdict?.verdict === 'block') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['clearance'],
        message: "a 'block' verdict cannot be cleared by a human sign-off",
      });
    }
  });
export type ShowcaseModeration = z.infer<typeof ShowcaseModeration>;

/**
 * A renderable file. `width`/`height` are the file's INTRINSIC pixel size, so a
 * card can reserve its box before the bytes decode — the same reason
 * `apps/marketing/src/content/media.ts` declares them for every static image.
 */
export const ShowcaseMedia = z.object({
  /** Where the bytes are served from — a site-absolute path or an absolute URL. */
  url: z.string().min(1).max(2048),
  contentType: z.string().min(1).max(128),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  /** Video only: clip length in ms. */
  durationMs: z.number().int().positive().optional(),
  /** Video only: the still a player shows before playback. */
  posterUrl: z.string().min(1).max(2048).optional(),
});
export type ShowcaseMedia = z.infer<typeof ShowcaseMedia>;

/**
 * One category the wall can be filtered by. `labelKey` is an i18n dot path, never
 * a display string — the marketing surface carries no inline copy, so a second
 * locale is a sibling message tree rather than a second manifest.
 */
export const ShowcaseCategory = z.object({
  key: slug,
  /** Dot path into the `marketing` i18n namespace. */
  labelKey: z.string().min(1).max(200),
});
export type ShowcaseCategory = z.infer<typeof ShowcaseCategory>;

/**
 * One card on the showcase wall.
 *
 * `prompt`/`style` are PROVENANCE — the actual generation input that produced
 * these bytes, not UI copy. They are recorded verbatim and are therefore not
 * translated: a translated prompt would no longer be a record of what was sent to
 * the provider. At least one of the two must be present, so no card can claim an
 * achievable output without saying how it was reached.
 */
export const ShowcaseItem = z
  .object({
    id: slug,
    kind: ShowcaseKind,
    /** Must match one of the manifest's declared {@link ShowcaseCategory} keys. */
    category: slug,
    audience: ShowcaseAudience,
    media: ShowcaseMedia,
    /** The generation prompt that produced this item, verbatim. */
    prompt: z.string().min(1).max(1000).optional(),
    /** The style/preset that produced it, when the recipe is a preset not a prompt. */
    style: z.string().min(1).max(200).optional(),
    /**
     * Where the card's CTA lands in the app, pre-seeded with this example — an
     * app-relative path (`/…`). Routing itself belongs to the app; this only
     * carries the destination so regenerating the corpus never edits a component.
     *
     * Checking only the first character is not enough. The WHATWG URL parser
     * treats a backslash as a slash for special schemes, so `/\evil.example/x`
     * resolves to `https://evil.example/x` — a card CTA on the crawlable landing
     * silently becoming an off-site redirect. Reject a leading `//` or `/\`, and
     * any backslash anywhere in the value.
     */
    deepLink: z
      .string()
      .min(1)
      .max(2048)
      .regex(
        /^\/(?![/\\])[^\\]*$/,
        'must be an app-relative path starting with a single "/" and containing no backslash',
      ),
    moderation: ShowcaseModeration,
  })
  .superRefine((item, ctx) => {
    if (!item.prompt && !item.style) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['prompt'],
        message: 'an item must record the prompt or the style that produced it',
      });
    }
    if (item.kind === 'video' && !item.media.durationMs) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['media', 'durationMs'],
        message: 'a video item must record its clip length',
      });
    }
  });
export type ShowcaseItem = z.infer<typeof ShowcaseItem>;

/**
 * The whole manifest. `version` is pinned so a consumer can refuse a shape it was
 * not built for rather than silently rendering half a card.
 */
export const ShowcaseManifest = z
  .object({
    version: z.literal(1),
    /** Tab order is this array's order. */
    categories: z.array(ShowcaseCategory).min(1).max(32),
    items: z.array(ShowcaseItem).max(512),
  })
  .superRefine((manifest, ctx) => {
    const categoryKeys = new Set<string>();
    manifest.categories.forEach((category, index) => {
      if (categoryKeys.has(category.key)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['categories', index, 'key'],
          message: `duplicate category key "${category.key}"`,
        });
      }
      categoryKeys.add(category.key);
    });

    const itemIds = new Set<string>();
    manifest.items.forEach((item, index) => {
      if (itemIds.has(item.id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['items', index, 'id'],
          message: `duplicate item id "${item.id}"`,
        });
      }
      itemIds.add(item.id);

      // A category the tabs don't declare renders an item nothing can reach.
      if (!categoryKeys.has(item.category)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['items', index, 'category'],
          message: `category "${item.category}" is not declared in categories`,
        });
      }
    });
  });
export type ShowcaseManifest = z.infer<typeof ShowcaseManifest>;

/**
 * Whether an item may be served from a public origin.
 *
 * The rule is the one docs/idea/compliance.md already imposes on the prebuilt
 * catalog: bytes reach a public URL only once the classifier cleared them or a
 * person signed off on those exact bytes. `unreviewed` is honest, and honestly
 * unpublishable.
 *
 * This is the PREDICATE only. The command that walks a manifest, recomputes each
 * `sha256` against the real bytes and refuses to publish on a mismatch is a
 * separate deliverable — the digest is recorded here precisely so that check has
 * something to verify against.
 */
export function isPublishable(item: ShowcaseItem): boolean {
  return item.moderation.clearance !== 'unreviewed';
}

/** Every item of `manifest` that {@link isPublishable} accepts, order preserved. */
export function publishableItems(manifest: ShowcaseManifest): readonly ShowcaseItem[] {
  return manifest.items.filter(isPublishable);
}

/**
 * The categories that still have something to show once `predicate` has filtered
 * the items — so a tab is never rendered over an empty wall. Order is the
 * manifest's declared tab order.
 */
export function populatedCategories(
  manifest: ShowcaseManifest,
  predicate: (item: ShowcaseItem) => boolean = isPublishable,
): readonly ShowcaseCategory[] {
  const present = new Set(manifest.items.filter(predicate).map((item) => item.category));
  return manifest.categories.filter((category) => present.has(category.key));
}
